const WIKIDATA_API_URL = 'https://www.wikidata.org/w/api.php';
const WIKIDATA_ENTITY_URL = 'https://www.wikidata.org/wiki/';
const WIKIPEDIA_SUMMARY_URL = 'https://en.wikipedia.org/api/rest_v1/page/summary/';
const COMMONS_FILE_URL = 'https://commons.wikimedia.org/wiki/Special:FilePath/';
const COMMONS_FILE_PAGE_URL = 'https://commons.wikimedia.org/wiki/File:';
const IUCN_SEARCH_URL = 'https://nrl.iucnredlist.org/search';
const IUCN_DONATE_URL = 'https://donate.iucnredlist.org/';
const IUCN_SPATIAL_DATA_URL = 'https://nrl.iucnredlist.org/resources/spatial-data-download';
const GBIF_SPECIES_URL = 'https://www.gbif.org/species/';
const INATURALIST_SPECIES_URL = 'https://www.inaturalist.org/taxa/';
const INATURALIST_SEARCH_URL = 'https://www.inaturalist.org/taxa/search?q=';

const STATUS_TONES = {
  'Least Concern': 'stable',
  'Near Threatened': 'watch',
  Vulnerable: 'watch',
  'Endangered status': 'risk',
  Endangered: 'risk',
  'Critically Endangered': 'critical',
  'Extinct in the Wild': 'critical',
  'Extinct species': 'critical',
  Extinct: 'critical',
  'Data Deficient': 'pending',
};

const profileCache = new Map();

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function buildCacheKey({ scientificName, commonName }) {
  return `${normalizeKey(scientificName)}|${normalizeKey(commonName)}`;
}

function buildUrl(base, params) {
  const url = new URL(base);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.json();
}

async function searchWikidata(query) {
  if (!query) return null;
  const payload = await fetchJson(
    buildUrl(WIKIDATA_API_URL, {
      action: 'wbsearchentities',
      search: query,
      language: 'en',
      format: 'json',
      origin: '*',
    })
  );
  const results = Array.isArray(payload?.search) ? payload.search : [];
  if (!results.length) return null;

  const exactMatch = results.find((item) => normalizeKey(item.label) === normalizeKey(query));
  return exactMatch || results[0];
}

async function getWikidataEntity(id) {
  if (!id) return null;
  const payload = await fetchJson(
    buildUrl(WIKIDATA_API_URL, {
      action: 'wbgetentities',
      ids: id,
      languages: 'en',
      props: 'labels|claims|sitelinks',
      format: 'json',
      origin: '*',
    })
  );
  return payload?.entities?.[id] || null;
}

async function getWikipediaSummary(title) {
  if (!title) return null;
  try {
    return await fetchJson(`${WIKIPEDIA_SUMMARY_URL}${encodeURIComponent(title)}`);
  } catch (_error) {
    return null;
  }
}

function getClaim(entity, property) {
  const claims = entity?.claims?.[property];
  return Array.isArray(claims) && claims.length ? claims[0] : null;
}

function getClaimValue(entity, property) {
  return getClaim(entity, property)?.mainsnak?.datavalue?.value ?? null;
}

function getItemClaimId(entity, property) {
  const value = getClaimValue(entity, property);
  if (!value || typeof value !== 'object') return '';
  return value.id || (value['numeric-id'] ? `Q${value['numeric-id']}` : '');
}

function getStringClaim(entity, property) {
  const value = getClaimValue(entity, property);
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function buildCommonsImageUrls(fileName) {
  if (!fileName) return null;
  return {
    src: `${COMMONS_FILE_URL}${encodeURIComponent(fileName)}`,
    pageUrl: `${COMMONS_FILE_PAGE_URL}${encodeURIComponent(fileName.replace(/ /g, '_'))}`,
    attributionLabel: 'Wikimedia Commons',
  };
}

function buildIucnSearchUrl(scientificName, commonName) {
  const query = scientificName || commonName;
  if (!query) return '';
  return buildUrl(IUCN_SEARCH_URL, {
    query,
    searchType: 'species',
  });
}

function buildSupportLinks({ scientificName, commonName, wikipediaUrl, wikidataId, gbifId, inaturalistId, iucnUrl }) {
  const links = [];
  const inaturalistSearch = scientificName || commonName
    ? `${INATURALIST_SEARCH_URL}${encodeURIComponent(scientificName || commonName)}`
    : '';

  if (iucnUrl) {
    links.push({
      label: 'IUCN assessment',
      href: iucnUrl,
      description: 'See the official Red List status, threats, and conservation notes.',
    });
  }

  if (gbifId) {
    links.push({
      label: 'GBIF open data',
      href: `${GBIF_SPECIES_URL}${gbifId}`,
      description: 'Explore open biodiversity records and occurrence context.',
    });
  }

  if (inaturalistId || inaturalistSearch) {
    links.push({
      label: 'Contribute sightings',
      href: inaturalistId ? `${INATURALIST_SPECIES_URL}${inaturalistId}` : inaturalistSearch,
      description: 'Use citizen science observations to help improve live species knowledge.',
    });
  }

  if (wikipediaUrl) {
    links.push({
      label: 'Species background',
      href: wikipediaUrl,
      description: 'Read the open encyclopedia summary and follow supporting references.',
    });
  }

  if (wikidataId) {
    links.push({
      label: 'Structured facts',
      href: `${WIKIDATA_ENTITY_URL}${wikidataId}`,
      description: 'Inspect the open linked-data record behind the status and cross-links.',
    });
  }

  links.push({
    label: 'Support conservation work',
    href: IUCN_DONATE_URL,
    description: 'Back species assessment and conservation work through the Red List.',
  });

  return links;
}

function buildFallbackProfile({ scientificName, commonName }) {
  const displayName = commonName || scientificName || 'This species';
  const iucnUrl = buildIucnSearchUrl(scientificName, commonName);
  return {
    summary: `${displayName} does not have a linked open encyclopedia profile yet, but Tellus can still help you explore when and where this species is surfacing in the current observation record.`,
    image: null,
    rangeMap: {
      src: '',
      pageUrl: IUCN_SPATIAL_DATA_URL,
      attributionLabel: 'IUCN spatial data',
    },
    iucnStatus: '',
    iucnStatusTone: 'pending',
    wikipediaUrl: '',
    supportLinks: buildSupportLinks({
      scientificName,
      commonName,
      wikipediaUrl: '',
      wikidataId: '',
      gbifId: '',
      inaturalistId: '',
      iucnUrl,
    }),
  };
}

export async function fetchSpeciesEnrichment({ scientificName, commonName }) {
  const cacheKey = buildCacheKey({ scientificName, commonName });
  if (profileCache.has(cacheKey)) {
    return profileCache.get(cacheKey);
  }

  const queries = [scientificName, commonName].filter(Boolean);
  if (!queries.length) {
    const fallback = buildFallbackProfile({ scientificName, commonName });
    profileCache.set(cacheKey, fallback);
    return fallback;
  }

  try {
    let searchResult = null;
    for (let index = 0; index < queries.length; index += 1) {
      searchResult = await searchWikidata(queries[index]);
      if (searchResult) break;
    }

    if (!searchResult?.id) {
      const fallback = buildFallbackProfile({ scientificName, commonName });
      profileCache.set(cacheKey, fallback);
      return fallback;
    }

    const entity = await getWikidataEntity(searchResult.id);
    const statusEntityId = getItemClaimId(entity, 'P141');
    const iucnId = getStringClaim(entity, 'P627');
    const gbifId = getStringClaim(entity, 'P846');
    const inaturalistId = getStringClaim(entity, 'P3151');
    const rangeMapFile = getStringClaim(entity, 'P181');
    const commonsHeroFile = getStringClaim(entity, 'P18');
    const wikipediaTitle = entity?.sitelinks?.enwiki?.title || '';

    const [statusEntity, wikipediaSummary] = await Promise.all([
      statusEntityId ? getWikidataEntity(statusEntityId) : Promise.resolve(null),
      getWikipediaSummary(wikipediaTitle),
    ]);

    const iucnStatus = statusEntity?.labels?.en?.value || '';
    const wikipediaUrl = wikipediaSummary?.content_urls?.desktop?.page || '';
    const wikipediaImage = wikipediaSummary?.originalimage?.source || wikipediaSummary?.thumbnail?.source || '';
    const commonsFallback = buildCommonsImageUrls(commonsHeroFile);
    const rangeMap = buildCommonsImageUrls(rangeMapFile);
    const iucnUrl = buildIucnSearchUrl(scientificName, commonName);

    const profile = {
      summary:
        wikipediaSummary?.extract ||
        `${commonName || scientificName || 'This species'} does not have a linked open encyclopedia summary yet, but Tellus can still show where it is surfacing in your observation data.`,
      image:
        wikipediaImage || commonsFallback
          ? {
              src: wikipediaImage || commonsFallback?.src || '',
              pageUrl: wikipediaUrl || commonsFallback?.pageUrl || '',
              attributionLabel: wikipediaUrl ? 'Wikipedia / Wikimedia' : commonsFallback?.attributionLabel || '',
            }
          : null,
      rangeMap:
        rangeMap || iucnUrl
          ? {
              src: rangeMap?.src || '',
              pageUrl: rangeMap?.pageUrl || IUCN_SPATIAL_DATA_URL,
              attributionLabel: rangeMap ? 'Open range map' : 'IUCN spatial data',
            }
          : null,
      iucnStatus,
      iucnReference: iucnId,
      iucnStatusTone: STATUS_TONES[iucnStatus] || 'pending',
      wikipediaUrl,
      supportLinks: buildSupportLinks({
        scientificName,
        commonName,
        wikipediaUrl,
        wikidataId: searchResult.id,
        gbifId,
        inaturalistId,
        iucnUrl,
      }),
    };

    profileCache.set(cacheKey, profile);
    return profile;
  } catch (_error) {
    const fallback = buildFallbackProfile({ scientificName, commonName });
    profileCache.set(cacheKey, fallback);
    return fallback;
  }
}

export function clearSpeciesEnrichmentCache() {
  profileCache.clear();
}
